import React, { useState } from 'react';
import SafeIcon from '../SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiAlertTriangle, FiCamera, FiCheck, FiX, FiUpload, FiRefreshCw, FiChevronDown, FiChevronUp, FiImage } = FiIcons;

/**
 * Chart Readability Error UI
 * Shown when the AI model returns a refusal or cannot read the chart properly
 */
const ChartReadabilityError = ({ errorCode, uiMessage, onRetry, onUploadNew, isRetrying = false }) => {
  const [showExamples, setShowExamples] = useState(false);

  // Common issues checklist
  const commonIssues = [
    { id: 'size', label: 'Image too small or compressed', icon: '📏' },
    { id: 'blur', label: 'Chart is blurry or pixelated', icon: '🔍' },
    { id: 'crop', label: 'Price scale or axes cropped out', icon: '✂️' },
    { id: 'time', label: 'Timeframe not visible', icon: '⏰' },
    { id: 'indicators', label: 'Too many indicators covering price', icon: '📊' },
    { id: 'mode', label: 'Dark mode glare or contrast issues', icon: '🌙' }
  ];

  // Tips for better screenshots
  const tips = [
    'Include the full price axis (right side)',
    'Make sure the timeframe label is visible',
    'Capture at least 50-100 recent candles',
    'Include the current session clearly',
    'Use TradingView\'s native screenshot feature (camera icon)'
  ];

  // What a good screenshot looks like
  const goodExamples = [
    { label: 'Visible price scale on right', icon: '📊' },
    { label: 'Timeframe label (1m, 5m, 1H, etc.)', icon: '⏰' },
    { label: 'Clean candlesticks without overlapping indicators', icon: '🕯️' },
    { label: 'Current session clearly visible', icon: '📍' }
  ];

  return (
    <div className="bg-gray-900/60 rounded-xl border border-red-500/30 backdrop-blur-sm overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-red-500/20">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-red-500/20 border border-red-500/30 flex items-center justify-center">
            <SafeIcon icon={FiAlertTriangle} className="w-6 h-6 text-red-400" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-red-400">We couldn't read this chart</h3>
            <p className="text-sm text-gray-400">The image quality may be insufficient for analysis</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-4">
        {/* Error Message */}
        {uiMessage && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
            <p className="text-sm text-red-300">{uiMessage}</p>
          </div>
        )}

        {/* Common Issues */}
        <div>
          <h4 className="text-sm font-semibold text-gray-300 mb-2 flex items-center gap-2">
            <SafeIcon icon={FiX} className="w-4 h-4 text-red-400" />
            Common Issues
          </h4>
          <div className="grid grid-cols-2 gap-2">
            {commonIssues.map((issue) => (
              <div key={issue.id} className="flex items-center gap-2 text-xs text-gray-400 bg-black/20 rounded-lg p-2">
                <span>{issue.icon}</span>
                <span>{issue.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Tips for Better Screenshots */}
        <div>
          <h4 className="text-sm font-semibold text-gray-300 mb-2 flex items-center gap-2">
            <SafeIcon icon={FiCheck} className="w-4 h-4 text-emerald-400" />
            For Best Results
          </h4>
          <ul className="space-y-1.5">
            {tips.map((tip, index) => (
              <li key={index} className="flex items-start gap-2 text-xs text-gray-400">
                <span className="text-emerald-400 mt-0.5">✓</span>
                <span>{tip}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Expandable: What a Good Screenshot Looks Like */}
        <div className="border border-purple-500/20 rounded-lg overflow-hidden">
          <button
            onClick={() => setShowExamples(!showExamples)}
            className="w-full flex items-center justify-between p-3 bg-gray-900/80 hover:bg-gray-800/80 transition-colors"
          >
            <div className="flex items-center gap-2">
              <SafeIcon icon={FiImage} className="w-4 h-4 text-purple-400" />
              <span className="text-sm font-medium text-purple-400">What a good screenshot looks like</span>
            </div>
            <SafeIcon icon={showExamples ? FiChevronUp : FiChevronDown} className="w-4 h-4 text-purple-400" />
          </button>
          
          {showExamples && (
            <div className="p-3 bg-gray-900/40 space-y-3">
              <div className="grid grid-cols-2 gap-2">
                {goodExamples.map((example, index) => (
                  <div key={index} className="flex items-center gap-2 text-xs text-purple-300 bg-purple-500/10 rounded-lg p-2 border border-purple-500/20">
                    <span>{example.icon}</span>
                    <span>{example.label}</span>
                  </div>
                ))}
              </div>
              <div className="text-xs text-gray-500 text-center mt-2">
                💡 Use TradingView's camera icon for the best quality
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="p-4 border-t border-gray-700/30 flex gap-3">
        <button
          onClick={onUploadNew}
          className="flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-gray-900/80 border border-purple-500/30 hover:bg-gray-800/80 hover:border-purple-500/50 transition-all text-white font-medium"
        >
          <SafeIcon icon={FiUpload} className="w-4 h-4 text-purple-400" />
          <span>Upload Clearer Screenshot</span>
        </button>
        
        {onRetry && (
          <button
            onClick={onRetry}
            disabled={isRetrying}
            className="flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-gray-900/80 border border-cyan-500/30 hover:bg-gray-800/80 hover:border-cyan-500/50 transition-all text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <SafeIcon icon={FiRefreshCw} className={`w-4 h-4 text-cyan-400 ${isRetrying ? 'animate-spin' : ''}`} />
            <span>{isRetrying ? 'Retrying...' : 'Retry'}</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default ChartReadabilityError;
